import { useEffect, useRef, useState } from "react";
import { useReducedMotion, motion } from "framer-motion";

const pills = [
  "EHR",
  "Imaging",
  "Worklists",
  "Reporting",
  "RCM",
  "Data / BI",
  "AI Models",
  "Agents",
];

const UserOrbit = () => {
  const reduce = useReducedMotion();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState(560);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setSize(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Leave room for pill width/height so they stay inside the wrapper
  const radius = Math.max(0, size / 2 - 40);
  const innerInset = size * 0.125;
  const anchorSize = size * 0.36;
  const pulseSize = size * 0.32;

  return (
    <div
      ref={wrapperRef}
      className="relative mx-auto w-full max-w-[560px] aspect-square"
    >
      {/* Dotted grid background */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            "radial-gradient(circle, hsl(var(--border) / 0.6) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          maskImage:
            "radial-gradient(circle at center, black 55%, transparent 85%)",
        }}
      />

      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="relative"
          style={{ width: radius * 2, height: radius * 2 }}
        >
          <div
            className="absolute inset-0 rounded-full border border-dashed border-border/70"
            aria-hidden
          />
          <div
            className="absolute rounded-full border border-dashed border-border/40"
            style={{ inset: innerInset }}
            aria-hidden
          />

          {/* Smooth pulse rings */}
          {!reduce &&
            [0, 3].map((delay) => (
              <motion.div
                key={delay}
                className="absolute left-1/2 top-1/2 rounded-full"
                style={{
                  width: pulseSize,
                  height: pulseSize,
                  marginLeft: -pulseSize / 2,
                  marginTop: -pulseSize / 2,
                  border: "1px solid hsl(var(--primary) / 0.35)",
                }}
                initial={{ scale: 0.78, opacity: 0 }}
                animate={{
                  scale: [0.78, 1.05, 1.55],
                  opacity: [0, 0.35, 0],
                }}
                transition={{
                  duration: 6,
                  delay,
                  repeat: Infinity,
                  ease: "easeOut",
                  times: [0, 0.18, 1],
                }}
                aria-hidden
              />
            ))}

          {/* Center anchor */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-card/90 backdrop-blur-sm ring-1 ring-border shadow-[0_10px_40px_-12px_hsl(var(--primary)/0.35)] flex flex-col items-center justify-center text-center px-4 md:px-6"
            style={{ width: anchorSize, height: anchorSize }}
          >
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              The user
            </span>
            <span className="mt-2 text-xs md:text-base font-medium text-foreground leading-snug">
              Your teams' workflow
            </span>
          </div>

          {/* Orbit pills — slow rotation, labels counter-rotate to stay horizontal */}
          <motion.div
            className="absolute inset-0"
            animate={reduce ? undefined : { rotate: 360 }}
            transition={
              reduce
                ? undefined
                : { duration: 70, repeat: Infinity, ease: "linear" }
            }
          >
            {pills.map((label, i) => {
              const angle = (i / pills.length) * 360 - 90;
              const rad = (angle * Math.PI) / 180;
              const left = radius + Math.cos(rad) * radius;
              const top = radius + Math.sin(rad) * radius;
              return (
                <div
                  key={label}
                  className="absolute"
                  style={{
                    left,
                    top,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <motion.div
                    className="inline-flex items-center gap-2 md:gap-2.5 rounded-full bg-card pl-2 pr-3 py-1.5 md:pl-2.5 md:pr-4 md:py-2 ring-1 ring-border shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)] whitespace-nowrap"
                    animate={reduce ? undefined : { rotate: -360 }}
                    transition={
                      reduce
                        ? undefined
                        : { duration: 70, repeat: Infinity, ease: "linear" }
                    }
                  >
                    <span className="inline-block h-2 w-2 md:h-2.5 md:w-2.5 rounded-full bg-primary" />
                    <span className="text-[11px] md:text-sm font-medium tracking-wide text-foreground">
                      {label}
                    </span>
                  </motion.div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default UserOrbit;
